import axios from "axios";
import Cookies from "js-cookie";
import { useCombinedStore } from "../store";

const api = axios.create({
  // Em produção vem do ambiente (o serviço no Render define REACT_APP_API_URL);
  // sem ela, cai no backend local, que é o que se usa no desenvolvimento.
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8081",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Variável para controlar se já está tentando refresh
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use((config) => {
  const token = useCombinedStore.getState().authUserZustand?.Token;
  // Sem `console.log` do token: ele é a credencial da sessão, e imprimi-la no
  // console de produção deixa a chave de acesso à rede inteira à mão de quem
  // abrir as ferramentas do navegador — ou de qualquer extensão instalada.
  if (token) {
    // SEMPRE envia o token, mesmo que grande
    // O servidor precisa lidar com tokens grandes de forma adequada
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Evitar processar em rotas públicas
    if (
      window.location.href.toString().includes("/login") ||
      window.location.href.toString().includes("/register")
    ) {
      return Promise.reject(error);
    }

    // Processar erros 401 (Unauthorized) e 431 (Request Header Fields Too Large)
    if (error.response?.status !== 401 && error.response?.status !== 431) {
      return Promise.reject(error);
    }

    // Se já está tentando refresh, adiciona à fila
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // Evitar loop de retry
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = Cookies.get("refreshToken");
    if (!refreshToken) {
      processQueue(new Error("No refresh token found"));
      isRefreshing = false;
      window.location.href = "/login";
      return Promise.reject(new Error("No refresh token found."));
    }

    try {
      // Para a requisição de refresh, não envie o token atual (pode ser muito grande)
      const refreshConfig = {
        headers: {
          Authorization: undefined,
        },
      };

      const response = await api.post(
        "/token",
        { refreshToken },
        refreshConfig
      );
      const { newAccessToken } = response.data;

      /*
       * ATUALIZA O TOKEN — com `updateToken`, e não com `setAuthUserZustand`.
       *
       * Estava assim:
       *
       *     setAuthUserZustand({ ...authUserZustand, Token: newAccessToken })
       *
       * e `setAuthUserZustand` espera `{ userInfo, authUser }`. Passando o
       * objeto do auth CRU, `data.authUser` é `undefined` — ou seja, a
       * renovação bem-sucedida APAGAVA `authUserZustand` e `userInfoZustand`
       * do store. A requisição que disparou o refresh até era refeita (o token
       * novo ia no header dela), mas a PRÓXIMA lia `authUserZustand?.Token`,
       * achava nada, e saía sem Authorization — 401 de novo, refresh de novo,
       * e o nome e a foto do topo sumiam junto.
       *
       * `updateToken` já existia no slice, fazendo exatamente a coisa certa, e
       * nunca tinha sido chamado por ninguém.
       */
      useCombinedStore.getState().updateToken(newAccessToken);

      // Processar fila de requisições pendentes
      processQueue(null, newAccessToken);

      // Atualizar header da requisição original com o NOVO token (que deve ser menor)
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      return api(originalRequest);
    } catch (err) {
      console.error("Token refresh failed:", err);
      processQueue(err, null);
      Cookies.remove("refreshToken");
      useCombinedStore.getState().removeAuthUserZustand();
      window.location.href = "/login";
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
